/** @jsxImportSource @emotion/react */
import { Alert, AlertColor, Slide, SlideProps, Snackbar } from '@mui/material'
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react'
import { setHandleCloseFn, setToastFn } from './toast'

type ToastPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'bottom-center'

export interface ToastOptions {
  type?: AlertColor
  title?: string | ReactNode
  description?: string
  position?: ToastPosition
  duration?: number | null
  children?: ReactNode
  open?: boolean
  showClose?: boolean
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void
  handleClose: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const SlideTransition = (props: SlideProps) => {
  const direction = props.direction || 'up'
  return <Slide {...props} direction={direction} />
}

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ToastOptions>({})

  const toast = useCallback((opts: ToastOptions) => {
    setOptions(opts)
    setOpen(true)
  }, [])

  const handleClose = () => setOpen(false)

  React.useEffect(() => {
    setToastFn(toast)
    setHandleCloseFn(handleClose)

    // Check for pending toast from client switch
    const pendingToast = localStorage.getItem('pending_toast')
    if (pendingToast) {
      try {
        const toastData = JSON.parse(pendingToast)
        localStorage.removeItem('pending_toast')

        // Show the toast
        toast({
          type: toastData.type,
          title: toastData.title,
          description: toastData.description,
          position: toastData.position,
          children: toastData.children,
          showClose: toastData.showClose,
        })
      } catch {
        // Ignore JSON parse errors
      }
    }
  }, [toast])

  const getAnchorOrigin = () => {
    const [vertical, horizontal] = (options.position || 'bottom-right').split('-') as [
      'top' | 'bottom',
      'left' | 'right' | 'center',
    ]
    return { vertical, horizontal }
  }

  return (
    <ToastContext.Provider value={{ toast, handleClose }}>
      {children}
      <Snackbar
        open={open}
        onClose={handleClose}
        autoHideDuration={options.duration === null ? null : options.duration || 4000}
        anchorOrigin={getAnchorOrigin()}
        TransitionComponent={SlideTransition}
        slotProps={{
          clickAwayListener: {
            onClickAway: (event: { defaultMuiPrevented?: boolean } & (MouseEvent | TouchEvent)) => {
              // Prevent's default 'onClickAway' behavior.
              event.defaultMuiPrevented = true
            },
          },
        }}
      >
        <Alert
          {...(options.showClose !== false && { onClose: handleClose })}
          severity={options.type || 'info'}
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
          variant="filled"
        >
          {options.title &&
            (typeof options.title === 'string' ? <strong>{options.title}</strong> : options.title)}
          {options.description && <div>{options.description}</div>}
          {options.children}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}

export const useToast = (): ((options: ToastOptions) => void) => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx.toast
}
